import { cn } from "tailwind-variants";
import type {
	ButtonUmbBlockElementModel,
	ButtonUmbBlockSettingsElementModel,
} from "#/_generated/umbraco/delivery-api/models";
import {
	Button,
	LinkButton,
	UmbLinkButton,
} from "#/_packages/umbrella/features/button/components/button.tsx";
import { tryGetButtonVariant } from "#/_packages/umbrella/features/button/utils";
import { buttonLinkDestination } from "#/_packages/umbrella/features/button/utils/link-destination";
import type { BlockItemProps } from "#/_packages/umbrella/integrations/umbraco/types";
import { toHoverItemCssClasses } from "#/_packages/umbrella/integrations/umbraco/utils/animation-utils.ts";
import { getUmbLink } from "#/_packages/umbrella/integrations/umbraco/utils/link-utils";
import { tryGetIntent, tryGetSize } from "#/_packages/umbrella/utils";

type ButtonUmbBlockProps = BlockItemProps & {
	content: ButtonUmbBlockElementModel;
	settings: ButtonUmbBlockSettingsElementModel;
	isHeaderDrawer?: boolean;
};

export default function ButtonUmbBlock({
	className,
	content,
	settings,
	isHeaderDrawer,
}: ButtonUmbBlockProps) {
	const link = content.properties?.link?.[0];
	const metadata = getUmbLink(link);
	const label = link?.title ?? "";
	const href = link?.url ?? undefined;
	const target = link?.target ?? undefined;
	const hideLabel = content.properties?.hideTextLabel ?? false;
	const size = tryGetSize(content.properties?.size);

	const sharedProps = {
		"data-tracking-event": metadata?.trackingEvent,
		size,
		intent: tryGetIntent(content.properties?.intentColor),
		variant: tryGetButtonVariant(content.properties?.typeVariant),
		displayBlock: content.properties?.displayBlock ?? false,
		hideLabel,
		className: cn(
			toHoverItemCssClasses(settings.properties?.animationHoverType),
			className,
		),
		id: settings.properties?.generatedId ?? content.id,
	};

	if (!content.properties?.icon && !label) {
		return null;
	}

	const buttonContent = () => {
		return link ? (
			link.route ? (
				<UmbLinkButton
					{...sharedProps}
					to={"/$"}
					{...buttonLinkDestination(link)}
					target={target}
					noFollow={metadata?.noFollow}
				>
					<Button.Content
						spriteIcon={content.properties?.icon}
						iconPosition={content.properties?.iconPosition}
						label={label}
						hideLabel={hideLabel}
					/>
				</UmbLinkButton>
			) : (
				<LinkButton
					{...sharedProps}
					href={href}
					target={target}
					noFollow={metadata?.noFollow}
				>
					<Button.Content
						spriteIcon={content.properties?.icon}
						iconPosition={content.properties?.iconPosition}
						label={label}
						hideLabel={hideLabel}
					/>
				</LinkButton>
			)
		) : (
			<Button {...sharedProps}>
				<Button.Content
					spriteIcon={content.properties?.icon}
					iconPosition={content.properties?.iconPosition}
					label={label}
					hideLabel={hideLabel}
				/>
			</Button>
		);
	};

	return isHeaderDrawer ? <div>{buttonContent()}</div> : buttonContent();
}
